/** White header for modals, slide-out panels, and full-screen detail views. */
'use client';

import { X } from 'lucide-react';
import { clsx } from 'clsx';

/**
 * Which surface the header sits on. Drives the close control's icon size and
 * accessible name, and the row's vertical alignment.
 *
 * - `panel` — SlideOutPanel / FullScreenModal (the original shape).
 * - `form`  — narrow form modals (`max-w-lg`): prominent title.
 * - `wide`  — wide content modals (`max-w-2xl`/`max-w-5xl`).
 *
 * BL-2305 gave all three the same header block (padding and title); density
 * now only picks the close glyph's size, its accessible name and the row's
 * vertical alignment.
 */
export type PanelHeaderDensity = 'panel' | 'form' | 'wide';

/**
 * One header block for every density (BL-2305): white, 20px above and below,
 * 24px sides — 16px below `md`, the same 768px the modal body's
 * `--overlay-gutter` switches at, so the header lines up with the body under it
 * (BL-1857) — and a 1px rule separating it from the content.
 */
const CONTAINER_CLASSES = 'bg-white border-b border-[var(--ink-100)] px-4 py-5 md:px-6';

/**
 * The modal-title role: `title` 20/28 · 600 in every density (ADR 063 — Type
 * Scale). BL-2305 retired `wide`'s quieter 16px step along with the teal bar it
 * was tuned against; the design owner chose one size for every modal header
 * on 2026-10-07. No `leading-*` here: the step carries its own line height.
 */
const TITLE_CLASSES = 'text-title font-semibold text-[var(--ink-900)] truncate';

/** Close-icon size per density — panels use a slightly smaller glyph. */
const ICON_CLASSES: Record<PanelHeaderDensity, string> = {
  panel: 'h-5 w-5',
  form: 'h-6 w-6',
  wide: 'h-6 w-6',
};

/**
 * The close control's foreground: `--ink-500` on the white bar (4.83:1 —
 * ADR 051 — Contrast Floor), darkening on hover:
 *   --ink-500 on white ........ 4.83:1  non-text ✓ (needs 3.0)
 *   --ink-900 on --ink-50 ..... 16.12:1 non-text ✓ (hover; needs 3.0)
 * The thumbnail tile's icon is --ink-500 on --ink-50 at 4.39:1, non-text ✓.
 *
 * BUG-1009: a modal once hand-rolled this button from `<Button variant="ghost">`
 * plus a colour override, and the two conflicting Tailwind utilities let the
 * wrong shade win — an X that matched its own background. Keeping the
 * treatment here, in the one shared header, is what stops that class of drift
 * from recurring per-modal.
 */
const CLOSE_CLASSES =
  'text-[var(--ink-500)] hover:text-[var(--ink-900)] hover:bg-[var(--ink-50)] transition-colors rounded-lg p-1 ' +
  'flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
  // A locked close (a save in flight) reads as locked, not as a live X.
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[var(--ink-500)]';

/**
 * Props shared by both heading shapes.
 */
interface PanelHeaderBaseProps {
  /** Secondary text below the title */
  subtitle?: React.ReactNode;
  /** Surface this header sits on. Defaults to `panel`. */
  density?: PanelHeaderDensity;
  /** `id` for the heading, so the dialog can point `aria-labelledby` at it */
  titleId?: string;
  /** Thumbnail element (image or icon) rendered on the left */
  thumbnail?: React.ReactNode;
  /** Metadata badges/tags rendered below the subtitle */
  metadata?: React.ReactNode;
  /** Action buttons rendered on the right side (below close button) */
  actions?: React.ReactNode;
  /** Callback when the close (X) button is clicked */
  onClose: () => void;
  /** Automation handle on the close control. */
  closeTestId?: string;
  /** Locks the close control while the caller's write is in flight. */
  closeDisabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Props for the PanelHeader component: a string `title`, or a caller-drawn
 * `heading` in its place.
 */
export type PanelHeaderProps = PanelHeaderBaseProps &
  (
    | {
        /** Primary heading text */
        title: string;
        heading?: never;
      }
    | {
        /**
         * Drawn in place of the title line, for a name that must render through
         * `CustomerIdentity` (ADR 058 — Customer Identity): Bidder approval's
         * header (BL-2692). It carries its own `title` type step.
         */
        heading: React.ReactNode;
        title?: never;
      }
  );

/**
 * The white header shared by every modal and panel that draws one (BL-2305).
 * Owns the layout for thumbnail, title, subtitle, metadata, actions, and the
 * close control — including the close treatment that individual modals kept
 * getting wrong while they each hand-rolled it (BUG-1009).
 * @param props - Title (or a drawn heading), density, optional thumbnail/subtitle/metadata/actions, and onClose.
 * @returns The header element.
 */
export default function PanelHeader({
  title,
  heading,
  subtitle,
  density = 'panel',
  titleId,
  thumbnail,
  metadata,
  actions,
  onClose,
  closeTestId,
  closeDisabled = false,
  className,
}: PanelHeaderProps) {
  return (
    <div className={clsx('flex-shrink-0', CONTAINER_CLASSES, className)}>
      <div
        className={clsx(
          'flex justify-between',
          density === 'panel' ? 'items-start' : 'items-center'
        )}
      >
        <div className="flex items-center space-x-4 min-w-0">
          {thumbnail && (
            <div className="h-14 w-14 rounded-lg bg-[var(--ink-50)] flex items-center justify-center text-[var(--ink-500)] overflow-hidden flex-shrink-0">
              {thumbnail}
            </div>
          )}
          <div className="flex-1 min-w-0">
            {heading ?? (
              <h3 id={titleId} className={TITLE_CLASSES}>
                {title}
              </h3>
            )}
            {/* A div, not a p: a caller's subtitle may itself be a block (BL-2692). */}
            {subtitle && (
              <div data-header-subtitle className="mt-0.5 text-body text-[var(--ink-500)] truncate">{subtitle}</div>
            )}
            {metadata && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-body">{metadata}</div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={closeDisabled}
          className={CLOSE_CLASSES}
          aria-label={density === 'panel' ? 'Close panel' : 'Close'}
          data-testid={closeTestId}
        >
          <X className={ICON_CLASSES[density]} aria-hidden="true" />
        </button>
      </div>
      {actions && (
        <div className="mt-4 flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}
